import primaryComponents from "../../../../components/primaryComponents";
import secondaryComponents from "../../../../components/secondaryComponents";
import { useAccountingBalanceSheet } from "./useAccountingBalanceSheet";
import { formatCurrency } from "../../../../helpers";
import styles from "./styles.module.scss";

const AccountingBalanceSheet = () => {
  const {
    asOfDate,
    reportData,
    isLoadingReport,
    storeList,
    storeHasMultipleCurrency,
    currentStore,
    setDateFilter,
    filterByStoreId,
    fetchBalanceSheetReport,
  } = useAccountingBalanceSheet();

  const currencyCode = reportData?.currency_code || "NGN";
  const formatAmount = (value) =>
    formatCurrency({ value: value ?? 0, currencyCode });

  const renderAccountItem = (account) => {
    const isNegative = account.net_balance < 0;
    return (
      <tr key={account.account_id} className={styles.lineItem}>
        <td className={styles.lineItem__name}>
          <span className={styles.lineItem__code}>{account.account_code} </span>
          {account.account_name}
        </td>
        <td
          className={`${styles.lineItem__amount} ${isNegative ? styles.lineItem__negative : ""}`}
        >
          {formatAmount(account.net_balance)}
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="d-flex justify-content-between p-3">
        <div style={{ width: "200px" }}>
          <primaryComponents.SelectField
            options={
              storeHasMultipleCurrency
                ? [...storeList]
                : [
                    {
                      name: "All stores",
                      id: "ALL_STORES",
                    },
                    ...storeList,
                  ]
            }
            idKey="id"
            nameKey="name"
            onChange={(value) => {
              filterByStoreId(value);
            }}
            value={
              storeList?.find(
                (storeItem) => `${storeItem?.id}` === `${currentStore}`,
              )?.name || "All stores"
            }
            floatError={false}
            classNames="store-select"
          />
        </div>
        <div className="d-flex align-items-center" style={{ gap: "12px" }}>
          <div>
            <label htmlFor="">As of Date</label>
            <primaryComponents.DateInput
              name="as_of_date"
              value={asOfDate || ""}
              onChange={({ target: { value } }) => {
                setDateFilter(value);
              }}
              placeholder="Select date"
            />
          </div>
          <div>
            <primaryComponents.Button
              classNames="btn btn--primary"
              onClick={fetchBalanceSheetReport}
              isLoading={isLoadingReport}
              loadingText="Generating..."
            >
              Generate Report
            </primaryComponents.Button>
          </div>
        </div>
      </div>

      <secondaryComponents.LoaderHelper
        isLoading={isLoadingReport}
        classNames="mt-4"
      >
        {!reportData ? (
          <div className={styles.emptyState}>
            Select a date and click Generate Report to view your Balance Sheet.
          </div>
        ) : (
          <div className={styles.reportCard}>
            <table className={styles.reportTable}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th className={styles.reportTable__totalCol}>Total</th>
                </tr>
              </thead>
              <tbody>
                {/* ── ASSETS ─────────────────────────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>ASSETS</td>
                </tr>

                {/* Current Assets */}
                <tr className={styles.subHeader}>
                  <td colSpan={2}>Current Assets</td>
                </tr>
                {[
                  ...(reportData?.asset_accounts?.vat_receivable || []),
                  ...(reportData?.asset_accounts?.cash_and_bank || []),
                  ...(reportData?.asset_accounts?.accounts_receivable || []),
                  ...(reportData?.asset_accounts?.inventory || []),
                ].map((account) => renderAccountItem(account))}
                <tr className={styles.subtotalRow}>
                  <td>Total Current Assets</td>
                  <td>{formatAmount(reportData?.total_current_assets)}</td>
                </tr>

                {/* Non Current Assets */}
                <tr className={styles.subHeader}>
                  <td colSpan={2}>Non Current Assets</td>
                </tr>
                {[
                  ...(reportData?.asset_accounts?.fixed_assets || []),
                  ...(reportData?.asset_accounts?.intangible_assets || []),
                ].map((account) => renderAccountItem(account))}
                {[
                  ...(reportData?.asset_accounts?.accumulated_depreciation ||
                    []),
                  ...(reportData?.asset_accounts?.accumulated_amortization ||
                    []),
                ].map((account) => (
                  <tr key={account.account_id} className={styles.lineItem}>
                    <td className={styles.lineItem__name}>
                      <span className={styles.lineItem__code}>
                        {account.account_code}{" "}
                      </span>
                      {account.account_name}
                    </td>
                    <td
                      className={`${styles.lineItem__amount} ${styles.lineItem__negative}`}
                    >
                      {formatAmount(account.net_balance)}
                    </td>
                  </tr>
                ))}
                <tr className={styles.subtotalRow}>
                  <td>Total Non Current Assets</td>
                  <td>{formatAmount(reportData?.total_non_current_assets)}</td>
                </tr>

                {/* Total Assets */}
                <tr className={styles.highlightRow}>
                  <td>TOTAL ASSETS</td>
                  <td>{formatAmount(reportData?.total_assets)}</td>
                </tr>
                <tr className={styles.thickDivider}>
                  <td colSpan={2} />
                </tr>

                {/* ── LIABILITIES & EQUITY ───────────────────────────────── */}
                <tr className={styles.sectionHeader}>
                  <td colSpan={2}>LIABILITIES & EQUITY</td>
                </tr>

                {/* Current Liabilities */}
                <tr className={styles.subHeader}>
                  <td colSpan={2}>Current Liabilities</td>
                </tr>
                {[
                  ...(reportData?.liability_accounts?.accounts_payable || []),
                  ...(reportData?.liability_accounts?.vat_payable || []),
                  ...(reportData?.liability_accounts?.accrued_expenses || []),
                  ...(reportData?.liability_accounts?.short_term_liabilities ||
                    []),
                ].map((account) => renderAccountItem(account))}
                <tr className={styles.subtotalRow}>
                  <td>Total Current Liabilities</td>
                  <td>{formatAmount(reportData?.total_current_liabilities)}</td>
                </tr>

                {/* Non Current Liabilities */}
                <tr className={styles.subHeader}>
                  <td colSpan={2}>Non Current Liabilities</td>
                </tr>
                {(
                  reportData?.liability_accounts?.long_term_liabilities || []
                ).map((account) => renderAccountItem(account))}
                {reportData?.liability_accounts?.long_term_liabilities
                  ?.length === 0 && (
                  <tr className={styles.lineItem}>
                    <td className={styles.lineItem__empty}>
                      No data available
                    </td>
                    <td className={styles.lineItem__empty}>–</td>
                  </tr>
                )}
                <tr className={styles.subtotalRow}>
                  <td>Total Non Current Liabilities</td>
                  <td>
                    {formatAmount(reportData?.total_non_current_liabilities)}
                  </td>
                </tr>

                {/* Equity */}
                <tr className={styles.subHeader}>
                  <td colSpan={2}>Equity</td>
                </tr>
                {[
                  ...(reportData?.equity_accounts?.retained_earnings || []),
                  ...(reportData?.equity_accounts?.owners_capital || []),
                ].map((account) => renderAccountItem(account))}
                {(reportData?.equity_accounts?.owners_drawings || []).map(
                  (account) => (
                    <tr key={account.account_id} className={styles.lineItem}>
                      <td className={styles.lineItem__name}>
                        <span className={styles.lineItem__code}>
                          {account.account_code}{" "}
                        </span>
                        {account.account_name}
                      </td>
                      <td
                        className={`${styles.lineItem__amount} ${styles.lineItem__negative}`}
                      >
                        {formatAmount(account.net_balance)}
                      </td>
                    </tr>
                  ),
                )}
                <tr className={styles.subtotalRow}>
                  <td>Total Equity</td>
                  <td>{formatAmount(reportData?.total_equity)}</td>
                </tr>

                {/* Total Liabilities & Equity */}
                <tr className={styles.netProfitRow}>
                  <td>TOTAL LIABILITIES & EQUITY</td>
                  <td>
                    {formatAmount(reportData?.total_liabilities_and_equity)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </secondaryComponents.LoaderHelper>
    </>
  );
};

export default AccountingBalanceSheet;
